import React from "react";
import type { SingleReportResults, EvaluationMetric } from "@services/EvaluationService";

interface SingleReportResultsProps {
  results: SingleReportResults;
  onClose?: () => void;
}

const SingleReportResults: React.FC<SingleReportResultsProps> = ({ results, onClose }) => {
  const getScoreColor = (score: number, maxScore: number = 10) => {
    const percentage = (score / maxScore) * 100;
    if (percentage >= 80) return "text-green-600 bg-green-50";
    if (percentage >= 60) return "text-yellow-600 bg-yellow-50";
    if (percentage >= 40) return "text-orange-600 bg-orange-50";
    return "text-red-600 bg-red-50";
  };

  const getScoreIcon = (score: number, maxScore: number = 10) => {
    const percentage = (score / maxScore) * 100;
    if (percentage >= 80) {
      return (
        <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
        </svg>
      );
    }
    if (percentage >= 60) {
      return (
        <svg className="w-5 h-5 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
        </svg>
      );
    }
    return (
      <svg className="w-5 h-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
      </svg>
    );
  };

  const renderMetricCard = (metricName: string, metric: EvaluationMetric, maxScore: number = 10) => (
    <div key={metricName} className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          {getScoreIcon(metric.score, maxScore)}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 capitalize">
              {metricName.replace(/_/g, ' ')}
            </h3>
            <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getScoreColor(metric.score, maxScore)}`}>
              {metric.score}/{maxScore}
              <span className="ml-1 text-xs">
                ({Math.round((metric.score / maxScore) * 100)}%)
              </span>
            </div>
          </div>
        </div>
      </div>
      
      <div className="space-y-3">
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-1">Description</h4>
          <p className="text-sm text-gray-600 leading-relaxed">{metric.description}</p>
        </div>
        
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-1">Evaluation Reason</h4>
          <p className="text-sm text-gray-600 leading-relaxed bg-gray-50 p-3 rounded-md border-l-4 border-blue-400">
            {metric.reason}
          </p>
        </div>
      </div>
    </div>
  );

  const metrics = Object.entries(results);
  const averageScore = metrics.reduce((sum, [, metric]) => sum + metric.score, 0) / metrics.length;
  const maxPossibleScore = 10; // Assuming max score is 10 for all metrics

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-gray-50 rounded-xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Report Evaluation Results</h2>
              <p className="text-gray-600 mt-1">Detailed analysis of your report quality</p>
            </div>
            
            {/* Overall Score */}
            <div className="text-right">
              <div className="text-3xl font-bold text-gray-900">
                {averageScore.toFixed(1)}/{maxPossibleScore}
              </div>
              <div className="text-sm text-gray-500">Overall Score</div>
              <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium mt-2 ${getScoreColor(averageScore, maxPossibleScore)}`}>
                {Math.round((averageScore / maxPossibleScore) * 100)}% Quality
              </div>
            </div>
            
            {onClose && (
              <button
                onClick={onClose}
                className="ml-4 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>
        
        {/* Results Grid */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {metrics.map(([metricName, metric]) => 
              renderMetricCard(metricName, metric, maxPossibleScore)
            )}
          </div>
        </div>
        
        {/* Footer */}
        <div className="bg-white border-t border-gray-200 p-4 flex justify-end space-x-3">
          <button
            onClick={() => {
              const resultsData = {
                overall_score: averageScore.toFixed(1),
                max_score: maxPossibleScore,
                metrics: results
              };
              
              const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(resultsData, null, 2));
              const downloadAnchorNode = document.createElement('a');
              downloadAnchorNode.setAttribute("href", dataStr);
              downloadAnchorNode.setAttribute("download", `report_evaluation_results_${new Date().getTime()}.json`);
              document.body.appendChild(downloadAnchorNode);
              downloadAnchorNode.click();
              downloadAnchorNode.remove();
            }}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            Export Results
          </button>
          {onClose && (
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors"
            >
              Close
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SingleReportResults;